/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React from 'react';
import { MRT_ColumnDef } from 'material-react-table';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { TaskData } from '../WorkTasks/WorkTasks';
import { IJobOverview } from '../JobsListView/JobsListView';
import { IJobs } from '../JobView/JobsView';
import { IBidList } from '../JobDetails/OneTimeJobDetails';
import { IProfileCardProps } from '../../../Customer/Components/ViewFacilityDetails/ViewFacilityDetails';

import { WorkOrder } from '../../../../API';
import { ISchedules, Task } from '../../../../Shared/Models/WorkTask.model';
import {
  AccordionInfo,
  AssociateAccordionDescription,
  AccordionWrapper,
  ServicesTitle,
  ServicesSubTitle,
  ServiceDetailsWrapper,
  ServiceTitleStatusWrapper,
  TitleStatusSeparator,
  StatusLabel,
} from '../../../../Shared/Components/Common/Accordion/Accordion.styles';
import AssociateOnTheJob from './AssociateOnTheJob';
import { FacilityInterface } from '../../../Company/Models/Company.Model';
import { AllShiftDetailsQuery, SchedulesByDateQuery } from './AssociateCard';
import { Box, CircularProgress } from '@mui/material';
import theme from '../../../../Shared/Themes/theme';
import { Icons } from '../../../../Shared/Constants/Icons';
import { getStatusLabelAndColor } from '../../../../Shared/Utilities/utils';
import { WorkDayStatus } from '../../../../Shared/Constants/App';
import { isEmpty } from 'lodash';

interface IDataItem {
  id: number;
  jobId?: string;
  isShowButton?: boolean;
  selectedRowCount?: number;
  taskCompleted?: number;
  tableData?: TaskData[];
  serviceName?: string;
  shiftTiming?: string;
  shiftType?: string;
  Jobname?: string;
  facilityName?: string;
  iconUrl?: any;
  categoryTitle?: any;
  totalTasks?: number;
  totalCompletedTasks?: any;
  options?: any;
}

export enum ScreenName {
  IssuesRaised = 'IssuesRaised',
  TasksReviewed = 'TaskReviewed',
  JobDetails = 'JobDetails',
}

export interface IEmployees {
  empShiftId: string;
  userId: string;
  userName: string;
  woShiftId: string;
}
interface IAccordionProps {
  vendorTypes?: [];
  accordionObj?: IDataItem;
  accordionObjNew: SchedulesByDateQuery;
  handleAccordion: (id: number) => (event: React.SyntheticEvent, expanded: boolean) => void;
  columns?: MRT_ColumnDef<TaskData | IJobOverview | IJobs | IBidList | IProfileCardProps>[];
  onChangeRow?: (rowSelection: object, data: TaskData[], service: any) => void;
  expanded: number | boolean;
  screenName: ScreenName;
  scheduleList: ISchedules[];
  handleApproveAll?: () => void;
  handleRejectAll?: () => void;
  dailyTaskData?: any;
  handleCustomerApproval?: (data: Task[]) => void;
  sendingForApproval?: boolean;
  isOtjJob?: boolean;
  optionsData?: any;
  jobData?: WorkOrder;
  FacilityData?: FacilityInterface;
  jobId: string;
  jobsLoader?: boolean;
  specificDate?: any;
  shiftDetails: AllShiftDetailsQuery[];
  isDataLoading: boolean;
  sectionTitle: string | null | undefined;
  scheduleSubName: string | null | undefined;
  shiftData: AllShiftDetailsQuery[];
  workOrderResponseData: any;
  employees: IEmployees[];
  employeeCount?: number;
  scheduleStatus?: string | null;
  isPrimeVendor?: boolean;
  woScheduleMasterId: string;
  workOrderId: string;
}

/**
 * @returns Accordion
 */
const AssociateOnTheJobAccordion = ({
  accordionObjNew,
  handleAccordion,
  expanded,
  screenName,
  scheduleList,
  jobData,
  FacilityData,
  jobId,
  jobsLoader,
  specificDate,
  isDataLoading,
  sectionTitle,
  scheduleSubName,
  shiftData,
  workOrderResponseData,
  employees,
  employeeCount,
  scheduleStatus,
  isPrimeVendor,
  woScheduleMasterId,
  workOrderId,
}: IAccordionProps) => {
  const { workOrderScheduleId = '' } = accordionObjNew ?? {};

  const expandStatus = scheduleList.findIndex((data) => data.id === workOrderScheduleId);
  const { label: statusLabel, color: statusColor } = getStatusLabelAndColor(scheduleStatus as WorkDayStatus);

  return (
    <AccordionWrapper expanded={expanded === expandStatus + 1} onChange={handleAccordion(expandStatus + 1)}>
      <AssociateAccordionDescription
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1bh-content"
        id="panel1bh-header">
        {screenName === ScreenName.JobDetails ? (
          isDataLoading ? (
            <CircularProgress size={30} sx={{ color: theme.palette.primary.dark }} />
          ) : (
            <ServiceDetailsWrapper>
              <ServiceTitleStatusWrapper>
                <ServicesTitle>{sectionTitle}</ServicesTitle>
                {!isEmpty(statusLabel) && (
                  <>
                    <TitleStatusSeparator>–</TitleStatusSeparator>
                    <StatusLabel bgColor={statusColor}>{statusLabel}</StatusLabel>
                  </>
                )}
                {!isPrimeVendor && employeeCount && (
                  <>
                    <TitleStatusSeparator>–</TitleStatusSeparator>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: '0.5rem' }}>
                      <img src={Icons.AddUserIcon} alt={'associates'} style={{ width: '1.5rem', height: '1.5rem' }} />
                      <ServicesTitle>{employeeCount}</ServicesTitle>
                    </Box>
                  </>
                )}
              </ServiceTitleStatusWrapper>
              <ServicesSubTitle>{scheduleSubName}</ServicesSubTitle>
            </ServiceDetailsWrapper>
          )
        ) : null}
      </AssociateAccordionDescription>
      <AccordionInfo>
        <AssociateOnTheJob
          jobData={jobData}
          FacilityData={FacilityData}
          jobId={jobId}
          jobsLoader={jobsLoader}
          specificDate={specificDate}
          workOrderScheduleId={workOrderScheduleId}
          woScheduleMasterId={woScheduleMasterId}
          scheduleData={accordionObjNew}
          shiftData={shiftData}
          workOrderResponseData={workOrderResponseData}
          employees={employees}
          isPrimeVendor={isPrimeVendor}
          originalWorkOrderId={workOrderId}
        />
      </AccordionInfo>
    </AccordionWrapper>
  );
};

export default AssociateOnTheJobAccordion;
